import { ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { Formik } from 'formik'
import { capitalize, isEmpty, keys, uniqBy } from 'lodash'

import {
  FilterHeader,
  FilterWrapper,
  TagsWrapper,
  FiltersWrapper,
  FilterToggle,
  FiltersContainer,
  FiltersSubmit,
  ApplyButtonWrapper,
  FilterButtonsWrapper,
  ResetButton
} from 'components/Filters'
import { InputTags } from 'components/v2/InputTags'
import { Select } from 'components/v2/Select'
import { Tag } from 'components/v2/Tag'
import { CityAutocomplete } from 'features/Cities/components/CityAutocomplete'
import { CompanyLineAutocomplete } from 'features/Lines/components/CompanyLineAutocomplete'
import { PlaceAutocomplete } from 'features/Places/components/PlaceAutocomplete'
import { saleStatusMessages } from 'features/Trips/components/SaleStatus/messages'
import { statusMessages } from 'features/Trips/components/Status/messages'
import { VehicleAutocomplete } from 'features/Vehicles/components/VehicleAutocomplete'
import { getQueryString } from 'utils/route'
import {
  GTBusinessClientAutocomplete,
  GTBusinessSiteSuggestion,
  GTRouteServiceSuggestion,
  GTRouteSuggestion,
  GTTripsV2DayOfWeek,
  GTTripV2DriverRsvpStatus,
  GTTripV2SaleStatus,
  GTTripV2Status,
  GTVehicleSuggestion
} from 'gql/types.generated'
import { IPartnerAutocomplete, MultiPartnerAutocomplete } from 'features/Partners/components/MultiPartnerAutocomplete'
import { DriverAutocomplete } from 'features/Drivers/components/DriverAutocomplete'
import { IDriverAutocomplete } from 'features/Drivers/components/MultiDriverAutocomplete'
import { Button } from 'components/v2/Button'
import { getDifference } from 'utils/object'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { ICompanyAutocomplete } from 'features/Companies/components/MultiCompanyAutocomplete'
import { getVehicleLabelWithExternalId } from 'features/Vehicles/utils'
import { RouteAutocomplete } from 'features/Routes/components/RouteAutocomplete'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'
import { BusinessSiteAutocomplete } from 'features/BusinessSites/components/BusinessSiteAutocomplete'
import { MultiRouteServiceAutocomplete } from 'features/Services/components/MultiRouteServiceAutocomplete'
import { getShortId } from 'utils/string'
import { SelectTags } from 'components/v2/SelectTags'
import { safeFormatMessage } from 'utils/intl'
import { startOfDay, subDays } from 'date-fns'
import { driverRSVPMessages } from 'features/Trips/components/DriverDetails/messages'

const STATUS_LIST = [
  GTTripV2Status.OnTime,
  GTTripV2Status.InTransit,
  GTTripV2Status.Completed,
  GTTripV2Status.Cancelled
]

const SALE_STATUS_LIST = [GTTripV2SaleStatus.Activated, GTTripV2SaleStatus.Deactivated]

export enum TripV2VehicleStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE'
}

const VEHICLE_STATUS_LIST = [TripV2VehicleStatus.Active, TripV2VehicleStatus.Inactive]

const DRIVER_RSVP_STATUS_LIST = [
  GTTripV2DriverRsvpStatus.Confirmed,
  GTTripV2DriverRsvpStatus.Declined,
  GTTripV2DriverRsvpStatus.Failed,
  GTTripV2DriverRsvpStatus.Missed,
  GTTripV2DriverRsvpStatus.Sent
]

const vehicleStatusMessages = defineMessages({
  [TripV2VehicleStatus.Active]: {
    id: 'dictionary.active',
    defaultMessage: 'Active'
  },
  [TripV2VehicleStatus.Inactive]: {
    id: 'dictionary.inactive',
    defaultMessage: 'Inactive'
  }
})

const messages = defineMessages({
  selectCompany: {
    id: 'trips.filters.selectCompany',
    defaultMessage: 'Select company'
  },
  selectCompanyLine: {
    id: 'trips.filters.selectCompanyLine',
    defaultMessage: 'Select company line'
  },
  selectOriginCity: {
    id: 'trips.filters.selectOriginCity',
    defaultMessage: 'Select origin city'
  },
  selectDestinationCity: {
    id: 'trips.filters.selectDestinationCity',
    defaultMessage: 'Select destination city'
  },
  serviceCodesPlaceholder: {
    id: 'trips.filters.serviceCodes.placeholder',
    defaultMessage: 'Select route services'
  },
  selectOrigin: {
    id: 'trips.filters.selectOrigin',
    defaultMessage: 'Select origin'
  },
  selectDestination: {
    id: 'trips.filters.selectDestination',
    defaultMessage: 'Select destination'
  },
  selectVehicle: {
    id: 'trips.filters.selectVehicle',
    defaultMessage: 'Select vehicle'
  },
  selectVehicleStatus: {
    id: 'trips.filters.selectVehicleStatus',
    defaultMessage: 'Select vehicle status'
  },
  selectStatus: {
    id: 'trips.filters.selectStatus',
    defaultMessage: 'Select status'
  },
  selectSellStatus: {
    id: 'trips.filters.selectSaleStatus',
    defaultMessage: 'Select sale status'
  },
  selectOption: {
    id: 'trips.filters.selectOption',
    defaultMessage: 'Select an option'
  },
  hasIncidences: {
    id: 'trips.filters.hasIncidences',
    defaultMessage: 'Has incidences'
  },
  yes: {
    id: 'dictionary.yes',
    defaultMessage: 'Yes'
  },
  no: {
    id: 'dictionary.no',
    defaultMessage: 'No'
  },
  idsPlaceholder: {
    id: 'trips.filters.ids.placeholder',
    defaultMessage: 'E.g: 12345678'
  },
  selectPartnersPlaceholder: {
    id: 'trips.filters.selectPartners.placeholder',
    defaultMessage: 'E.g: Partner name'
  },
  selectDriver: {
    id: 'trips.filters.selectDriver.placeholder',
    defaultMessage: 'Select driver'
  },
  selectRoute: {
    id: 'trips.filters.selectRoute',
    defaultMessage: 'Select route'
  },
  selectBusinessClient: {
    id: 'trips.filters.selectBusinessClient',
    defaultMessage: 'Select business client'
  },
  selectBusinessSite: {
    id: 'trips.filters.selectBusinessSite',
    defaultMessage: 'Select business site'
  },
  selectDow: {
    id: 'trips.filters.selectDow',
    defaultMessage: 'Select day of week'
  },
  withReservedModality: {
    id: 'trips.filters.withReservedModality',
    defaultMessage: 'With reserved modality'
  },
  withoutReservedModality: {
    id: 'trips.filters.withoutReservedModality',
    defaultMessage: 'Without reserved modality'
  },
  selectDepartureHourRange: {
    id: 'trips.filters.selectDepartureHourRange',
    defaultMessage: 'Select departure hour range'
  },
  selectDriverRSVPStatus: {
    id: 'trips.filters.selectDriverRSVPStatus',
    defaultMessage: 'Select attendance pass status'
  }
})

const defaultValues = {
  company: { id: '', name: '' },
  companyLine: { id: '', name: '' },
  originCity: { id: '', name: '' },
  destinationCity: { id: '', name: '' },
  routeServices: [],
  origin: { id: '', name: '' },
  destination: { id: '', name: '' },
  vehicle: { id: '', name: '', externalID: '' },
  vehicleStatus: '',
  hasIncidences: '',
  status: '',
  saleStatus: '',
  ids: [],
  partners: [],
  driver: { id: '', name: '' },
  routes: [],
  businessClient: { id: '', name: '' },
  businessSite: { id: '', name: '' },
  departureDaysOfWeek: [],
  departureHourRange: { startHour: '', endHour: '' },
  driverRSVPStatus: ''
}

const DOW_LIST = [
  GTTripsV2DayOfWeek.Monday,
  GTTripsV2DayOfWeek.Tuesday,
  GTTripsV2DayOfWeek.Wednesday,
  GTTripsV2DayOfWeek.Thursday,
  GTTripsV2DayOfWeek.Friday,
  GTTripsV2DayOfWeek.Saturday,
  GTTripsV2DayOfWeek.Sunday
]

const getFilteredDaysOfWeek = (values: GTTripsV2DayOfWeek[]): GTTripsV2DayOfWeek[] => {
  return DOW_LIST.filter((day) => {
    return values.indexOf(day) === -1
  })
}

export const TripFilters = ({ filters, departureDateFrom, departureDateTo, getTripsQuery }: TSFixMe): ReactElement => {
  const navigate = useNavigate()
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): void => {
    showFilters(!isShowingFilters)
  }

  const filterTrips = (values: TSFixMe): void => {
    const query = getTripsQuery({
      departureDateFrom,
      departureDateTo,
      filters: values
    })

    navigate(`/trips?${getQueryString(query)}`)
  }

  const initialValues = {
    company: filters.company || defaultValues.company,
    companyLine: filters.companyLine || defaultValues.companyLine,
    originCity: filters.originCity || defaultValues.originCity,
    destinationCity: filters.destinationCity || defaultValues.destinationCity,
    routeServices: filters.routeServices || defaultValues.routeServices,
    origin: filters.origin || defaultValues.origin,
    destination: filters.destination || defaultValues.destination,
    vehicle: filters.vehicle || defaultValues.vehicle,
    vehicleStatus: filters.vehicleStatus || defaultValues.vehicleStatus,
    hasIncidences: filters.hasIncidences ?? defaultValues.hasIncidences,
    status: filters.status || defaultValues.status,
    saleStatus: filters.saleStatus || defaultValues.saleStatus,
    ids: filters.ids || defaultValues.ids,
    partners: filters.partners || defaultValues.partners,
    driver: filters.driver || defaultValues.driver,
    routes: filters.routes || defaultValues.routes,
    businessClient: filters.businessClient || defaultValues.businessClient,
    businessSite: filters.businessSite || defaultValues.businessSite,
    departureDaysOfWeek: filters.departureDaysOfWeek || defaultValues.departureDaysOfWeek,
    departureHourRange: filters.departureHourRange || defaultValues.departureHourRange,
    driverRSVPStatus: filters.driverRSVPStatus || defaultValues.driverRSVPStatus
  }

  const handleSubmit = (): void => {
    // Do nothing, only apply filters when apply button is pressed
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      {(props): ReactElement => {
        const { handleChange, handleSubmit, setFieldValue, setValues, values } = props

        const handleIdsChange = (ids: string[]): void => {
          setFieldValue(
            'ids',
            ids.map((id) => getShortId(id))
          )
        }

        const handleSelectChange = (event: TSFixMe): void => {
          handleChange(event)
        }

        const handleFilterDelete = (id: keyof typeof defaultValues): void => {
          setFieldValue(id, defaultValues[id])
        }

        const handleResetFilters = (): void => {
          filterTrips(defaultValues)
          setValues(defaultValues)
        }

        const handleDaysOfWeekChange = (daysOfWeek: GTTripsV2DayOfWeek[]): void => {
          setFieldValue('departureDaysOfWeek', daysOfWeek)
        }

        const hasPendingFilters = !isEmpty(getDifference(initialValues, values))

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <FilterButtonsWrapper>
                    <FilterToggle onClick={toggleFilters} />
                    <ResetButton onClick={handleResetFilters} />
                  </FilterButtonsWrapper>
                  <TagsWrapper>
                    {keys(values).map((filterKey): TSFixMe => {
                      let label, value
                      const filterValue = (values as TSFixMe)[filterKey]

                      const hasValue = typeof filterValue === 'boolean' || !isEmpty(filterValue)

                      if (!hasValue) return null

                      switch (filterKey) {
                        case 'company':
                          label = <FormattedMessage id="dictionary.company" defaultMessage="Company" />
                          value = filterValue.name
                          break
                        case 'hasIncidences':
                          label = <FormattedMessage id="trips.filters.hasIncidences" defaultMessage="Has incidences" />
                          value = filterValue ? intl.formatMessage(messages.yes) : intl.formatMessage(messages.no)
                          break
                        case 'companyLine':
                          label = <FormattedMessage id="dictionary.companyLine" defaultMessage="Company line" />
                          value = filterValue.name
                          break
                        case 'ids':
                          label = <FormattedMessage id="trips.filters.ids.label" defaultMessage="Trip Ids" />
                          value = filterValue.join(', ')
                          break
                        case 'status':
                          label = <FormattedMessage id="dictionary.status" defaultMessage="Status" />
                          value =
                            statusMessages[filterValue as GTTripV2Status] &&
                            intl.formatMessage(statusMessages[filterValue as GTTripV2Status])
                          break
                        case 'saleStatus':
                          label = <FormattedMessage id="dictionary.saleStatus" defaultMessage="Sale Status" />
                          value =
                            saleStatusMessages[filterValue as GTTripV2SaleStatus] &&
                            intl.formatMessage(saleStatusMessages[filterValue as GTTripV2SaleStatus])
                          break
                        case 'originCity':
                          label = <FormattedMessage id="dictionary.originCity" defaultMessage="Origin city" />
                          value = filterValue.name
                          break
                        case 'destinationCity':
                          label = <FormattedMessage id="dictionary.destinationCity" defaultMessage="Destination city" />
                          value = filterValue.name
                          break
                        case 'routeServices':
                          label = (
                            <FormattedMessage id="trips.filters.serviceCodes.label" defaultMessage="Service codes" />
                          )
                          value = uniqBy(filterValue as GTRouteServiceSuggestion[], 'code')
                            .map((service): string => service.code)
                            .join(', ')
                          break
                        case 'origin':
                          label = <FormattedMessage id="dictionary.origin" defaultMessage="Origin" />
                          value = filterValue.name
                          break
                        case 'destination':
                          label = <FormattedMessage id="dictionary.destination" defaultMessage="Destination" />
                          value = filterValue.name
                          break
                        case 'vehicle':
                          label = <FormattedMessage id="dictionary.vehicle" defaultMessage="Vehicle" />
                          value = filterValue.name
                          break
                        case 'vehicleStatus':
                          label = <FormattedMessage id="dictionary.vehicleStatus" defaultMessage="Vehicle Status" />
                          value = safeFormatMessage({ intl, messages: vehicleStatusMessages, key: filterValue })
                          break
                        case 'partners':
                          label = <FormattedMessage id="dictionary.partners" defaultMessage="Partners" />
                          value = filterValue.map((partner: IPartnerAutocomplete): string => partner.name).join(', ')
                          break
                        case 'driver':
                          label = <FormattedMessage id="dictionary.driver" defaultMessage="Driver" />
                          value = filterValue.name
                          break
                        case 'routes':
                          label = <FormattedMessage id="dictionary.route" defaultMessage="Route" />
                          value = (filterValue as GTRouteSuggestion[]).at(0)?.name
                          break
                        case 'businessClient':
                          label = <FormattedMessage id="dictionary.businessClient" defaultMessage="Business client" />
                          value = filterValue.name
                          break
                        case 'businessSite':
                          label = <FormattedMessage id="dictionary.businessSite" defaultMessage="Business site" />
                          value = filterValue.name
                          break
                        case 'departureDaysOfWeek':
                          label = <FormattedMessage id="dictionary.dow" defaultMessage="Dow" />
                          value = filterValue.map((dow: GTTripsV2DayOfWeek): string => capitalize(dow)).join(', ')
                          break
                        case 'driverRSVPStatus':
                          label = (
                            <FormattedMessage
                              id="trips.filters.driverRSVPStatus.label"
                              defaultMessage="Attendance Pass Status"
                            />
                          )
                          value = safeFormatMessage({ intl, messages: driverRSVPMessages, key: filterValue })
                          break
                      }

                      if (!value) return null

                      return (
                        <Tag
                          key={filterKey}
                          onDelete={handleFilterDelete.bind(this, filterKey as keyof typeof defaultValues)}
                        >
                          {label}: {value}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                  <ApplyButtonWrapper>
                    <Button
                      kind="primary"
                      type="button"
                      disabled={!hasPendingFilters}
                      onClick={(): void => filterTrips(values)}
                    >
                      <FormattedMessage id="dictionary.apply" defaultMessage="Apply" />
                    </Button>
                  </ApplyButtonWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <CompanyAutocomplete
                    placeholder={intl.formatMessage(messages.selectCompany)}
                    value={values.company.name}
                    label={<FormattedMessage id="dictionary.company" defaultMessage="Company" />}
                    onChange={(company: ICompanyAutocomplete): void => {
                      setValues({
                        ...values,
                        company
                      })
                    }}
                  />
                  <CompanyLineAutocomplete
                    placeholder={intl.formatMessage(messages.selectCompanyLine)}
                    value={values.companyLine.name}
                    label={<FormattedMessage id="dictionary.companyLine" defaultMessage="Company line" />}
                    onChange={(companyLine: TSFixMe): void => {
                      setFieldValue('companyLine', companyLine)
                    }}
                  />
                  <BusinessClientAutocomplete
                    placeholder={intl.formatMessage(messages.selectBusinessClient)}
                    value={values.businessClient.name}
                    label={<FormattedMessage id="dictionary.businessClient" defaultMessage="Business client" />}
                    onChange={(businessClient: GTBusinessClientAutocomplete): void => {
                      setFieldValue('businessClient', businessClient)
                    }}
                  />
                  <BusinessSiteAutocomplete
                    placeholder={intl.formatMessage(messages.selectBusinessSite)}
                    value={values.businessSite.name}
                    label={<FormattedMessage id="dictionary.businessSite" defaultMessage="Business site" />}
                    onChange={(businessSite: GTBusinessSiteSuggestion): void => {
                      setFieldValue('businessSite', businessSite)
                    }}
                  />
                  <InputTags
                    id="ids"
                    values={values.ids}
                    onChange={handleIdsChange}
                    placeholder={intl.formatMessage(messages.idsPlaceholder)}
                    label={<FormattedMessage id="dictionary.ids" defaultMessage="Ids" />}
                  />
                  <Select
                    id="status"
                    value={values.status}
                    onChange={handleSelectChange}
                    label={<FormattedMessage id="dictionary.status" defaultMessage="Status" />}
                  >
                    <option value="">{intl.formatMessage(messages.selectStatus)}</option>

                    {STATUS_LIST.map((status): TSFixMe => {
                      return (
                        <option value={status} key={status}>
                          {intl.formatMessage(statusMessages[status])}
                        </option>
                      )
                    })}
                  </Select>
                  <Select
                    id="saleStatus"
                    value={values.saleStatus}
                    onChange={handleSelectChange}
                    label={<FormattedMessage id="dictionary.saleStatus" defaultMessage="Sale status" />}
                  >
                    <option value="">{intl.formatMessage(messages.selectSellStatus)}</option>

                    {SALE_STATUS_LIST.map((saleStatus): TSFixMe => {
                      return (
                        <option value={saleStatus} key={saleStatus}>
                          {intl.formatMessage(saleStatusMessages[saleStatus])}
                        </option>
                      )
                    })}
                  </Select>
                  <RouteAutocomplete
                    placeholder={intl.formatMessage(messages.selectRoute)}
                    value={values.routes.at(0)?.name}
                    label={<FormattedMessage id="dictionary.route" defaultMessage="Route" />}
                    onChange={(routes): void => {
                      setFieldValue('routes', routes)
                    }}
                    uniqueName
                  />
                  <CityAutocomplete
                    placeholder={intl.formatMessage(messages.selectOriginCity)}
                    value={values.originCity.name}
                    label={<FormattedMessage id="dictionary.originCity" defaultMessage="Origin city" />}
                    onChange={(city: TSFixMe): void => {
                      setFieldValue('originCity', city)
                    }}
                  />
                  <CityAutocomplete
                    placeholder={intl.formatMessage(messages.selectDestinationCity)}
                    value={values.destinationCity.name}
                    label={<FormattedMessage id="dictionary.destinationCity" defaultMessage="Destination city" />}
                    onChange={(city: TSFixMe): void => {
                      setFieldValue('destinationCity', city)
                    }}
                  />
                  <MultiRouteServiceAutocomplete
                    placeholder={intl.formatMessage(messages.serviceCodesPlaceholder)}
                    values={values.routeServices}
                    onChange={(routeServices: GTRouteServiceSuggestion[]): void => {
                      setFieldValue('routeServices', routeServices)
                    }}
                    label={<FormattedMessage id="trips.filters.serviceCodes.label" defaultMessage="Service codes" />}
                    uniqueCode
                    endAt={{ start: subDays(startOfDay(new Date()), 30) }}
                  />
                  <PlaceAutocomplete
                    placeholder={intl.formatMessage(messages.selectOrigin)}
                    value={values.origin.name}
                    label={<FormattedMessage id="dictionary.origin" defaultMessage="Origin" />}
                    onChange={(place: TSFixMe): void => {
                      setFieldValue('origin', place)
                    }}
                  />
                  <PlaceAutocomplete
                    placeholder={intl.formatMessage(messages.selectDestination)}
                    value={values.destination.name}
                    label={<FormattedMessage id="dictionary.destination" defaultMessage="Destination" />}
                    onChange={(place: TSFixMe): void => {
                      setFieldValue('destination', place)
                    }}
                  />
                  <VehicleAutocomplete
                    placeholder={intl.formatMessage(messages.selectVehicle)}
                    value={getVehicleLabelWithExternalId(values.vehicle)}
                    label={<FormattedMessage id="dictionary.vehicle" defaultMessage="Vehicle" />}
                    onChange={(vehicle: GTVehicleSuggestion): void => {
                      setFieldValue('vehicle', {
                        id: vehicle?.id || defaultValues.vehicle.id,
                        name: vehicle?.name || defaultValues.vehicle.name,
                        externalID: vehicle?.externalID || defaultValues.vehicle.externalID
                      })
                    }}
                  />
                  <Select
                    id="vehicleStatus"
                    value={values.vehicleStatus}
                    onChange={handleSelectChange}
                    label={<FormattedMessage id="dictionary.vehicleStatus" defaultMessage="Vehicle status" />}
                  >
                    <option value="">{intl.formatMessage(messages.selectVehicleStatus)}</option>
                    {VEHICLE_STATUS_LIST.map((vehicleStatus): ReactElement => {
                      return (
                        <option value={vehicleStatus} key={vehicleStatus}>
                          {safeFormatMessage({ intl, messages: vehicleStatusMessages, key: vehicleStatus })}
                        </option>
                      )
                    })}
                  </Select>
                  <Select
                    id="hasIncidences"
                    value={values.hasIncidences}
                    onChange={handleSelectChange}
                    label={<FormattedMessage id="dictionary.hasIncidences" defaultMessage="Has incidences" />}
                  >
                    <option value="">{intl.formatMessage(messages.selectOption)}</option>
                    <option value="true">{intl.formatMessage(messages.yes)}</option>
                    <option value="false">{intl.formatMessage(messages.no)}</option>
                  </Select>
                  <MultiPartnerAutocomplete
                    label={<FormattedMessage id="dictionary.partners" defaultMessage="Partners" />}
                    placeholder={intl.formatMessage(messages.selectPartnersPlaceholder)}
                    values={values.partners}
                    onChange={(partners: IPartnerAutocomplete[]): void => {
                      setFieldValue('partners', partners)
                    }}
                  />
                  <DriverAutocomplete
                    label={<FormattedMessage id="dictionary.driver" defaultMessage="Driver" />}
                    placeholder={intl.formatMessage(messages.selectDriver)}
                    onChange={(driver: IDriverAutocomplete): void => {
                      setFieldValue('driver', driver)
                    }}
                    value={values.driver.name}
                    isShowingWithoutDriverOption
                  />
                  <SelectTags
                    id="departureDaysOfWeek"
                    placeholder={intl.formatMessage(messages.selectDow)}
                    values={values.departureDaysOfWeek}
                    items={getFilteredDaysOfWeek(values.departureDaysOfWeek)}
                    getTagLabel={(dow: GTTripsV2DayOfWeek): string => {
                      return capitalize(dow)
                    }}
                    onChange={handleDaysOfWeekChange}
                    label={<FormattedMessage id="dictionary.dow" defaultMessage="Dow" />}
                  />
                  <Select
                    id="driverRSVPStatus"
                    value={values.driverRSVPStatus}
                    onChange={handleSelectChange}
                    label={
                      <FormattedMessage
                        id="trips.filters.driverRSVPStatus.label"
                        defaultMessage="Attendance Pass Status"
                      />
                    }
                  >
                    <option value="">{intl.formatMessage(messages.selectDriverRSVPStatus)}</option>
                    {DRIVER_RSVP_STATUS_LIST.map((driverRSVPStatus): ReactElement => {
                      return (
                        <option value={driverRSVPStatus} key={driverRSVPStatus}>
                          {safeFormatMessage({ intl, messages: driverRSVPMessages, key: driverRSVPStatus })}
                        </option>
                      )
                    })}
                  </Select>
                </FiltersContainer>
              </FiltersWrapper>
            </div>
            <FiltersSubmit />
          </form>
        )
      }}
    </Formik>
  )
}
